import React, { useState, useEffect, useRef } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js";
import Alert from '../common/Alert.js';
import loanGivenService from '../../services/LoanGivenService.js';
import axiosInstance from '../../config/axios.js';
import { Plus, ArrowLeft, Pencil, Package, HandCoins } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const LoanGivenList = () => {
  const { t } = useTranslation();
  const [loans, setLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [borrowerTypeFilter, setBorrowerTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [currentPage, statusFilter, borrowerTypeFilter, userFilter, searchQuery, overdueOnly]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true&status=active');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (statusFilter) filters.status = statusFilter;
      if (borrowerTypeFilter) filters.borrower_type = borrowerTypeFilter;
      if (userFilter) filters.user_id = userFilter;
      if (searchQuery) filters.search = searchQuery;
      if (overdueOnly) filters.overdue_only = 'true';

      const response = await loanGivenService.getAll(currentPage, perPage, filters);
      setLoans((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load loan records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(
      `Approve loan for "${borrowerName}"?`,
      'Approve Loan'
    );

    if (confirmed) {
      try {
        await loanGivenService.approve(loanId);
        Alert.success('Loan approved successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async (loanId, borrowerName) => {
    const reason = await Alert.prompt(
      `Enter rejection reason for "${borrowerName}":`,
      'Reject Loan'
    );

    if (reason !== null) {
      try {
        await loanGivenService.reject(loanId, reason || 'No reason provided');
        Alert.success('Loan rejected successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject loan');
      }
    }
  };

  const handleActivate = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(
      `Activate loan for "${borrowerName}"?`,
      'Activate Loan'
    );

    if (confirmed) {
      try {
        await loanGivenService.activate(loanId);
        Alert.success('Loan activated successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(
      `Delete loan record for "${borrowerName}"?`,
      'Delete Loan'
    );

    if (confirmed) {
      try {
        await loanGivenService.delete(loanId);
        Alert.success('Loan deleted successfully!');
        
        if (loans.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchLoans();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const toggleDropdown = (loanId) => {
    setActiveDropdown(activeDropdown === loanId ? null : loanId);
  };

  const getLoanGivenExtraItems = (loan) => {
    const items = [];
    if (loan.status === 'pending') {
      items.push(
        { label: 'Approve', icon: '✅', onClick: () => handleApprove(loan.loan_id, loan.borrower_name) },
        { label: 'Reject', icon: '❌', onClick: () => handleReject(loan.loan_id, loan.borrower_name) },
      );
    }
    if (loan.status === 'approved') {
      items.push({ label: 'Activate', icon: '🔓', onClick: () => handleActivate(loan.loan_id, loan.borrower_name) });
    }
    if (loan.status === 'approved' || loan.status === 'active') {
      items.push({
        label: 'Add Repayment',
        icon: '💰',
        onClick: () => navigate(`/loans-given/repayment/${loan.loan_id}`),
      });
    }
    if (!['completed', 'rejected'].includes(loan.status)) {
      items.push({
        label: t('common.edit'),
        icon: '✏️',
        onClick: () => navigate(`/loans-given/edit/${loan.loan_id}`),
      });
    }
    return items;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700',
      rejected: 'bg-red-100 text-red-700',
      defaulted: 'bg-red-200 text-red-800',
      cancelled: 'bg-gray-200 text-gray-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const getBorrowerTypeBadge = (type) => {
    const typeColors = {
      employee: 'bg-purple-100 text-purple-700',
      external_company: 'bg-indigo-100 text-indigo-700',
      individual: 'bg-pink-100 text-pink-700'
    };
    
    return typeColors[type] || 'bg-gray-100 text-gray-700';
  };

  const hasActiveFilters = statusFilter || borrowerTypeFilter || userFilter || searchQuery || overdueOnly;

  const clearFilters = () => {
    setStatusFilter('');
    setBorrowerTypeFilter('');
    setUserFilter('');
    setSearchQuery('');
    setOverdueOnly(false);
    setCurrentPage(1);
  };

  return (
    <DashboardLayout>
                <ListPageHeader
                  title="Expenses & Loans"
                  icon={HandCoins}
                  titleClassName="text-secondary"
                  iconClassName="w-6 h-6 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-6"
                >
                  <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/loans-given/add')}>
                    Add Loan
                  </Button>
                </ListPageHeader>


                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
                    <SearchSubmitInput
                      value={searchQuery}
                      onSearch={(value) => {
                        setSearchQuery(value);
                        setCurrentPage(1);
                      }}
                      placeholder="Loan ID, Name, Company..."
                      inputClassName="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Borrower Type</label>
                    <select
                      value={borrowerTypeFilter}
                      onChange={(e) => {
                        setBorrowerTypeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    >
                      <option value="">All Types</option>
                      <option value="employee">Employee</option>
                      <option value="external_company">External Company</option>
                      <option value="individual">Individual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                      <option value="defaulted">Defaulted</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Employee</label>
                    <select
                      value={userFilter}
                      onChange={(e) => {
                        setUserFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    >
                      <option value="">All Employees</option>
                      {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.name} - {user.user_id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm w-full py-2">
                      <input
                        type="checkbox"
                        checked={overdueOnly}
                        onChange={(e) => {
                          setOverdueOnly(e.target.checked);
                          setCurrentPage(1);
                        }}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-blue-400"
                      />
                      <span className="text-gray-700">Show Overdue Only</span>
                    </label>
                  </div>
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading loans..." />
                  </div>
                ) : loans.length === 0 ? (
                  <div className="p-8 text-center text-gray-600">
                    No loan records found
                  </div>
                ) : (
                  <>
                    {/* Table - Desktop View */}
                    <div className="hidden lg:block">
                      <div className="bg-white rounded-xl shadow-sm">
                        <div className="min-w-full">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loan ID</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Borrower</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.type')}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {loans.map((loan) => (
                                <tr key={loan.loan_id} className={`${loan.is_overdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'} transition-colors`}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Button variant="link" onClick={() => navigate(`/loans-given/view/${loan.loan_id}`)} className={listIdLinkClass}>
                                      {loan.loan_id}
                                    </Button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{loan.borrower_name}</div>
                                    {loan.borrower_company && (
                                      <div className="text-sm text-gray-600">{loan.borrower_company}</div>
                                    )}
                                    {loan.user_details && (
                                      <div className="text-sm text-gray-600">{loan.user_details.user_id}</div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getBorrowerTypeBadge(loan.borrower_type)}`}>
                                      {loan.borrower_type.replace(/_/g, ' ').toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {formatCurrency(loan.loan_amount)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                                    {formatCurrency(loan.amount_pending)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-600">{formatDate(loan.due_date)}</div>
                                    {loan.is_overdue && (
                                      <span className="text-xs text-red-600 font-semibold">⚠️ OVERDUE</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(loan.status)}`}>
                                      {loan.status.toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap relative">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === loan.loan_id}
                                      onToggle={() => toggleDropdown(loan.loan_id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/loans-given/view/${loan.loan_id}`)}
                                      onDelete={
                                        ['pending', 'rejected'].includes(loan.status)
                                          ? () => handleDelete(loan.loan_id, loan.borrower_name)
                                          : undefined
                                      }
                                      extraItems={getLoanGivenExtraItems(loan)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="bg-gray-50 px-6 py-4 border-t">
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-700">
                              Showing {loans.length} record{loans.length !== 1 ? 's' : ''}
                            </div>
                            <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="lg:hidden space-y-3 sm:space-y-4">
                      {loans.map((loan) => (
                        <div key={loan.loan_id} className={`bg-white rounded-xl shadow-sm p-4 sm:p-5 ${loan.is_overdue ? 'border border-red-300' : ''}`}>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <button
                                onClick={() => navigate(`/loans-given/view/${loan.loan_id}`)}
                                className={listIdLinkMobileClass}
                              >
                                {loan.loan_id}
                              </button>
                              <p className="text-sm font-medium text-gray-900 mt-1 truncate">{loan.borrower_name}</p>
                              {loan.borrower_company && (
                                <p className="text-xs text-gray-600 truncate">{loan.borrower_company}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadge(loan.status)}`}>
                                {loan.status.toUpperCase()}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === loan.loan_id}
                                onToggle={() => toggleDropdown(loan.loan_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/loans-given/view/${loan.loan_id}`)}
                                onDelete={
                                  ['pending', 'rejected'].includes(loan.status)
                                    ? () => handleDelete(loan.loan_id, loan.borrower_name)
                                    : undefined
                                }
                                extraItems={getLoanGivenExtraItems(loan)}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">Type:</span>
                              <p className="text-gray-900 font-medium capitalize">{loan.borrower_type.replace(/_/g, ' ')}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Loan Amount:</span>
                              <p className="text-gray-900 font-semibold">{formatCurrency(loan.loan_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Pending:</span>
                              <p className="text-red-600 font-semibold">{formatCurrency(loan.amount_pending)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Due Date:</span>
                              <p className={`font-medium ${loan.is_overdue ? 'text-red-600' : 'text-gray-900'}`}>
                                {formatDate(loan.due_date)}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Interest:</span>
                              <p className="text-gray-900 font-medium">{loan.interest_rate}% p.a.</p>
                            </div>
                          </div>

                          {loan.is_overdue && (
                            <div className="mt-3 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700 font-semibold flex items-center gap-1">
                              ⚠️ OVERDUE
                            </div>
                          )}
                        </div>
                      ))}

                      
                    </div>
                  </>
                )}
    </DashboardLayout>
  );
};

export default LoanGivenList;